import { useContext, useEffect, useState } from "react";
import {
  AlertComponent,
  DialogComponent,
  ModalComponent,
  UploadFileForm,
} from "../../../components";
import { Button } from "@mui/material";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import { useAppDesignPdf, useBusyAction, useRequest } from "../../../hooks";
import {
  DeleteAction,
  DownloadAction,
  EditAction,
  OpenAction,
} from "../../../components/shared/DocRowActions";
import { usePaginator } from "../../../hooks/usePaginator";
import {
  ClientProps,
  DocumentProps,
  WellProps,
  DocRow,
} from "../../../interfaces/interfaces";
import {
  ListPageLayout,
  ListColumn,
  ListFilterField,
} from "../../../components/shared/ListPageLayout";
import DataContext from "../../../context/DataContext";
import { useNavigate } from "react-router-dom";

const isSuccessful = (r: any) => r?.success || r?.succes;
const getRows = (r: any) =>
  Array.isArray(r?.data) ? r.data : r?.data?.rows ?? [];

const fmtDate = (d: string) => {
  if (!d) return "—";
  const dt = new Date(d.includes("T") ? d : d + "T00:00:00");
  if (isNaN(dt.getTime())) return "—";
  return `${dt.getMonth() + 1}/${dt.getDate()}/${dt.getFullYear()}`;
};

const latestUploadByPullingId = (rows: DocRow[]) => {
  const map = new Map<number, DocRow>();
  rows.forEach((row) => {
    const pid = row.pullingDesignId;
    if (!pid) return;
    const cur = map.get(pid);
    if (!cur || new Date(row.date).getTime() > new Date(cur.date).getTime())
      map.set(pid, row);
  });
  return map;
};

export const PullingReportAdmin = () => {
  const navigate = useNavigate();
  const { handleRequest } = useRequest();
  const { downloadPullingPdf } = useAppDesignPdf();
  const { clientOptions, fetchDataClient, onDeleteDocument } = useContext(DataContext);
  const { paginationModel, setPaginationModel } = usePaginator("pulling-report-admin", 8);

  const [wells, setWells] = useState<WellProps[]>([]);
  const [rows, setRows] = useState<DocRow[]>([]);
  const [wellsLoading, setWellsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [selectedWellId, setSelectedWellId] = useState<number | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreatingReport, setIsCreatingReport] = useState(false);
  const [isError, setIsError] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [idToDelete, setIdToDelete] = useState("");
  const [isOpenAlert, setIsOpenAlert] = useState(false);
  const [selectedClient, setSelectedClient] = useState<ClientProps | null>(null);

  // Filter/search state — previously owned internally by DocumentsPortalLayout,
  // now lifted up because ListPageLayout is a controlled component: it renders
  // whatever `rows` slice it's given and expects the caller to own filtering,
  // search and pagination.
  const [docSearch, setDocSearch] = useState("");
  const [source, setSource] = useState(""); // "" = all
  const [owner, setOwner] = useState(""); // "" = all
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  useEffect(() => { fetchDataClient(); }, []);

  useEffect(() => {
    setTimeout(() => { setIsError(false); setIsSuccess(false); }, 4000);
  }, [isError, isSuccess]);

  const handleClientChange = (client: ClientProps | null) => {
    setSelectedClient(client);
    setSelectedWellId(null);
    if (!client) { setWells([]); setRows([]); return; }
    const opts: RequestInit = { method: "GET" };
    setWellsLoading(true);
    setRowsLoading(true);
    Promise.all([
      handleRequest({ endpoint: `well/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `pulling-list/client/${client.id}`, options: opts }),
      handleRequest({ endpoint: `pulling-detail?client=${client.id}`, options: opts }),
    ])
      .then(([wellsRes, uploadedRes, appRes]) => {
        const wellData: WellProps[] = isSuccessful(wellsRes) ? wellsRes.data : [];
        setWells(wellData);
        setWellsLoading(false);
        const wellMap = new Map<number, string>(wellData.map((w) => [w.id as number, w.name]));
        // NOTE: owner is hardcoded to "—" on the Admin side (both uploaded and app
        // rows) because the backend `pulling`/`pullingDesign` Sequelize models have
        // no `createdBy`/user field at all. This is a real backend data gap, out of
        // scope for this frontend-only migration — do not invent owner data here.
        const uploaded: DocRow[] = (isSuccessful(uploadedRes) ? uploadedRes.data : []).map(
          (item: any) => ({
            rowId: `u-${item.id}`,
            customName: item.customName,
            date: item.date ?? "",
            installationDate: item.installationDate,
            wellId: item.idWell,
            wellName: wellMap.get(item.idWell) ?? "",
            source: "uploaded" as const,
            path: item.path,
            owner: "—",
            pullingDesignId: item.pullingDesignId,
          }),
        );
        const uploadsByPulling = latestUploadByPullingId(uploaded);
        const app: DocRow[] = getRows(appRes).map((item: any) => {
          const def = uploadsByPulling.get(item.id);
          return {
            rowId: `a-${item.id}`,
            customName: item.customName || item.pullingName,
            date: item.createdAt || item.pullingDate || "",
            installationDate: item.installationDate,
            wellId: item.well?.id,
            wellName: item.well?.name ?? "",
            source: "app" as const,
            appId: item.id,
            owner: "—",
            defaultUploadPath: def?.path,
            defaultUploadName: def?.customName,
          };
        });
        const sorted = [...uploaded, ...app].sort(
          (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
        );
        setRows(sorted);
        setRowsLoading(false);
      })
      .catch(() => { setIsError(true); setWellsLoading(false); setRowsLoading(false); });
  };

  const fetchPullingByKey = (key: string, name: string, download: boolean) => {
    setIsCreatingReport(true);
    const opts: RequestInit = {
      method: "POST",
      body: JSON.stringify({ key }),
      headers: { responseType: "Buffer" } as any,
    };
    return handleRequest({ endpoint: "pdf-pulling-adm", options: opts })
      .then((res) => {
        if (res.success) {
          const url = window.URL.createObjectURL(
            new Blob([new Uint8Array(res.data.data).buffer], { type: "application/pdf;charset=utf-8" }),
          );
          if (download) {
            const link = document.createElement("a");
            link.href = url; link.setAttribute("download", name);
            document.body.appendChild(link); link.click();
          } else {
            window.open(url);
          }
        } else setIsError(true);
      })
      .catch(() => setIsError(true))
      .finally(() => setIsCreatingReport(false));
  };

  const handleDownload = async (row: DocRow): Promise<void> => {
    if (row.source === "uploaded" && row.path) {
      await fetchPullingByKey(row.path, row.customName, true);
    } else if (row.source === "app") {
      if (row.defaultUploadPath) {
        await fetchPullingByKey(row.defaultUploadPath, row.defaultUploadName ?? row.customName, true);
      } else if (row.appId != null) {
        await downloadPullingPdf(row.appId, row.customName);
      }
    }
  };
  const { isBusy: isDownloading, run: runDownload } = useBusyAction(handleDownload);

  const handleUploadPulling = async ({
    client, well, file, customName, installationDate,
  }: DocumentProps) => {
    try {
      setIsCreatingReport(true);
      setIsModalOpen(false);
      const formData = new FormData();
      formData.append("client", JSON.stringify(client));
      formData.append("well", JSON.stringify(well));
      formData.append("type", "pulling");
      customName && formData.append("customName", customName);
      installationDate && formData.append("installationDate", installationDate.toISOString());
      formData.append("file", file, file.name);
      const res = await (
        await fetch(process.env.REACT_APP_DEV_API + "/pulling-upload", {
          method: "POST",
          body: formData,
          headers: {
            Authorization: `Bearer ${localStorage.getItem("info")}`,
          },
        })
      ).json();
      if (res.success) { setIsSuccess(true); selectedClient && handleClientChange(selectedClient); }
      else setIsError(true);
    } catch { setIsError(true); }
    finally { setIsCreatingReport(false); }
  };

  // ---- Derived: well filter -> search -> source/owner/date filters ----
  const filteredByWell =
    selectedWellId != null ? rows.filter((r) => r.wellId === selectedWellId) : rows;

  let filtered = docSearch.trim()
    ? filteredByWell.filter((r) =>
        r.customName.toLowerCase().includes(docSearch.trim().toLowerCase()),
      )
    : filteredByWell;
  if (source) filtered = filtered.filter((r) => r.source === source);
  if (owner) filtered = filtered.filter((r) => r.owner === owner);
  if (dateFrom || dateTo) {
    filtered = filtered.filter((r) => {
      const d = new Date(r.date + "T12:00:00");
      if (dateFrom && d < new Date(dateFrom + "T00:00:00")) return false;
      if (dateTo && d > new Date(dateTo + "T23:59:59")) return false;
      return true;
    });
  }

  const ownerOptions = Array.from(new Set(rows.map((r) => r.owner))).filter(
    (o) => o && o !== "—",
  );

  const totalCount = filtered.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / paginationModel.pageSize));
  const currentPage = Math.min(paginationModel.page, totalPages - 1);
  const pageRows = filtered.slice(
    currentPage * paginationModel.pageSize,
    (currentPage + 1) * paginationModel.pageSize,
  );

  const columns: ListColumn<DocRow>[] = [
    { key: "customName", header: "Pulling Report", render: (r) => r.customName, width: "2fr" },
    { key: "wellName", header: "Well", render: (r) => r.wellName || "—", width: "160px" },
    { key: "date", header: "Date", render: (r) => fmtDate(r.date), width: "120px" },
    {
      key: "source",
      header: "Source",
      render: (r) => (r.source === "app" ? "App Design" : "Uploaded"),
      width: "110px",
    },
    { key: "owner", header: "Created by", render: (r) => r.owner, width: "160px" },
  ];

  const filters: ListFilterField[] = [
    {
      kind: "select",
      key: "source",
      label: "Source",
      value: source,
      onChange: setSource,
      options: [
        { value: "app", label: "App Design" },
        { value: "uploaded", label: "Uploaded" },
      ],
    },
    { kind: "date", key: "dateFrom", label: "From", value: dateFrom, onChange: setDateFrom },
    { kind: "date", key: "dateTo", label: "To", value: dateTo, onChange: setDateTo },
  ];
  // Owner is hardcoded to "—" for every row on the Admin side (see note above), so
  // ownerOptions is always empty here — the "Created by" filter is intentionally
  // never shown on Admin, matching the reference files' conditional-render pattern.
  if (ownerOptions.length > 0) {
    filters.splice(1, 0, {
      kind: "select",
      key: "owner",
      label: "Created by",
      value: owner,
      onChange: setOwner,
      options: ownerOptions.map((o) => ({ value: o, label: o })),
    });
  }

  return (
    <>
      <ListPageLayout<DocRow>
        title="Pulling Reports"
        subtitle="Browse, filter and download pulling reports"
        columns={columns}
        rows={pageRows}
        getRowId={(r) => r.rowId}
        rowsLoading={rowsLoading}
        isError={isError}
        emptyTitle={!selectedClient ? "Select a client to load their pulling reports" : undefined}
        searchValue={docSearch}
        onSearchChange={setDocSearch}
        searchPlaceholder="Search pulling reports…"
        filters={filters}
        page={currentPage}
        pageSize={paginationModel.pageSize}
        totalCount={totalCount}
        onPageChange={(page) => setPaginationModel({ ...paginationModel, page })}
        onPageSizeChange={(pageSize) => setPaginationModel({ page: 0, pageSize })}
        rowsPerPageOptions={[8, 15, 25, 50]}
        sidePanel={{
          title: "Wells",
          items: wells.map((w) => ({ id: w.id ?? -1, label: w.name })),
          selectedId: selectedWellId,
          onSelect: (id) => {
            setSelectedWellId(id as number | null);
            setPaginationModel({ ...paginationModel, page: 0 });
          },
          loading: wellsLoading,
          searchPlaceholder: "Search wells…",
        }}
        toolbarActions={
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {clientOptions?.length ? (
              <select
                value={selectedClient ? String(selectedClient.id) : ""}
                onChange={(e) => {
                  const c =
                    clientOptions.find((cl) => String(cl.id) === e.target.value) ?? null;
                  handleClientChange(c);
                }}
              >
                <option value="" disabled>Select a client...</option>
                {clientOptions.map((c) => (
                  <option key={c.id} value={String(c.id)}>{c.name}</option>
                ))}
              </select>
            ) : null}
            <Button startIcon={<UploadFileRoundedIcon />} onClick={() => setIsModalOpen(true)}>
              Upload
            </Button>
          </div>
        }
        rowActions={(r) =>
          r.source === "uploaded" ? (
            <>
              <OpenAction
                rowId={r.rowId}
                onClick={() => r.path && fetchPullingByKey(r.path, r.customName, false)}
              />
              <DownloadAction rowId={r.rowId} busy={isDownloading} onClick={() => runDownload(r)} />
              <DeleteAction
                rowId={r.rowId}
                onClick={() => { setIdToDelete(r.rowId.replace("u-", "")); setIsOpenAlert(true); }}
              />
            </>
          ) : (
            <>
              <DownloadAction
                rowId={r.rowId}
                busy={isDownloading}
                title="Download PDF"
                onClick={() => runDownload(r)}
              />
              <EditAction
                rowId={r.rowId}
                title="Edit Design"
                onClick={() => navigate(`/pulling/${r.appId}`)}
              />
            </>
          )
        }
      />

      {isCreatingReport && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1300 }} />
      )}
      {isSuccess && <AlertComponent type="success" />}
      {isModalOpen && (
        <ModalComponent
          modalContent={
            <UploadFileForm
              clientOptions={clientOptions}
              wellOptions={[]}
              type="pulling"
              fetchDataWells={(id) => handleRequest({ endpoint: `well/client/${id}`, options: { method: "GET" } })}
              onUploadFile={(v: DocumentProps) => handleUploadPulling(v)}
              onCancel={() => setIsModalOpen(false)}
            />
          }
        />
      )}
      {isOpenAlert && (
        <DialogComponent
          type="error"
          modalContent={<div>Are you sure you want to remove this Pulling Report from the Cloud?</div>}
          onAccept={() => {
            onDeleteDocument("pulling-file", idToDelete, () => selectedClient && handleClientChange(selectedClient));
            setIdToDelete(""); setIsOpenAlert(false);
          }}
          onCancel={() => { setIsOpenAlert(false); setIdToDelete(""); }}
        />
      )}
    </>
  );
};
